import { useCallback, useEffect, useState } from 'react';
import type { SchoolsResponse } from '@ucp/web/lib';
import { NotSignedInError, useApiFetch } from './useApiFetch';

/**
 * Call `GET /schools` (PRO-0030) via the shared `useApiFetch` hook, which attaches
 * auth and the ambient `x-ucp-school-id` header. Re-fetches when the active school
 * changes (the switcher drives it). The `SchoolsResponse` type is shared with the
 * API via `@ucp/web/lib` (type-only → erased from the bundle).
 */
export type SchoolsState =
  | { status: 'loading' }
  | { status: 'ready'; data: SchoolsResponse }
  | { status: 'error'; message: string };

export function useSchools(activeSchoolId: string | null): SchoolsState {
  const apiFetch = useApiFetch();
  const [state, setState] = useState<SchoolsState>({ status: 'loading' });

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const response = await apiFetch('/schools');
      if (!response) {
        return;
      }
      if (response.status === 403) {
        setState({ status: 'error', message: 'You do not have access to that school.' });
        return;
      }
      if (response.status === 503) {
        setState({
          status: 'error',
          message: 'The platform is temporarily unavailable. Please try again shortly.',
        });
        return;
      }
      if (!response.ok) {
        setState({ status: 'error', message: `Unexpected error (HTTP ${response.status}).` });
        return;
      }

      const data = (await response.json()) as SchoolsResponse;
      setState({ status: 'ready', data });
    } catch (error) {
      const message =
        error instanceof NotSignedInError
          ? error.message
          : error instanceof Error
            ? error.message
            : 'Could not contact the platform.';
      setState({ status: 'error', message });
    }
  }, [apiFetch, activeSchoolId]);

  useEffect(() => {
    void load();
  }, [load]);

  return state;
}
